import { useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";

/**
 * Function signature for updating the URL.
 * @param {string} [pathname] - The new pathname for the URL.
 * @param {URLSearchParams} [searchParams] - The search parameters for the URL.
 * @param {boolean} [push] - Whether to use push or replace.
 * Note: The default behavior is history.replace if the `push` parameter is not specified.
 * @returns {void}
 */
interface UpdateUrlFn {
  (pathname?: string, searchParams?: URLSearchParams, push?: boolean): void;
}

interface UpdateUrlHook {
  (
    defaultPathname?: string,
    defaultSearchParams?: URLSearchParams,
  ): {
    updateUrl: UpdateUrlFn;
  };
}

/**
 * Custom hook that provides the `updateUrl` and `replaceUrl` functions for updating the URL.
 * @param {string} [defaultPathname] - The default pathname for the URL.
 * @param {URLSearchParams} [defaultSearchParams] - The default search parameters for the URL.
 * @returns {UpdateUrlHook} An object with `updateUrl` and `replaceUrl` functions.
 */
const useUpdateUrl: UpdateUrlHook = (defaultPathname?: string, defaultSearchParams?: URLSearchParams) => {
  const history = useHistory();
  const isMountedRef = useRef<boolean>(false);

  useEffect(() => {
    isMountedRef.current = true;

    // Perform initial update with default values if provided
    if (defaultPathname && defaultSearchParams) {
      updateUrl(defaultPathname, defaultSearchParams);
    }

    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * This function adds a new entry in the history stack.
   * Navigates to the URL using the provided pathname and search parameters.
   * If the component is not mounted, the update is skipped.
   * @param {string} [pathname] - The new pathname for the URL.
   * @param {URLSearchParams} [searchParams] - The search parameters for the URL.
   *  * @param {boolean} [push] - Whether to use push or replace.
   * Note: The default behavior is history.replace if the `push` parameter is not specified.
   * @returns {void}
   */
  const updateUrl: UpdateUrlFn = (pathname?: string, searchParams?: URLSearchParams, push: boolean = false): void => {
    if (!isMountedRef.current) return;

    const finalPathname = pathname || defaultPathname;
    const finalSearchParams = searchParams || defaultSearchParams;

    if (finalPathname && finalSearchParams) {
      const historyAction = push ? history.push : history.replace;
      historyAction({ pathname: finalPathname, search: finalSearchParams.toString() });
    }
  };

  return { updateUrl };
};

export default useUpdateUrl;
